import i18n from '@/i18n';
import { notificationService } from '@/services/notification/notification-service';
import { GraphRequestError } from '@/services/graph/graph-request-error';
import { sharePointDriveService } from '@/services/sharepoint-drive/sharepoint-drive-service';
import { directoryKeys } from '@/utils/queryKeys';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { getParentPath, isAncestorOrSelf } from '../utils';
import type { FileManagerNavigationState } from '../logic/core/useFileManagerNavigation';
import type { FileSystemItem } from '../types';

const { t } = i18n.global;

export type RenameItemVariables = {
  item: FileSystemItem;
  newName: string;
};

// `navigation` is taken as an explicit param rather than self-injected via
// useFileManagerNavigation(), for the same cross-injection reason useMoveItemMutation/
// useDeleteItemMutation both document: this mutation is constructed inside
// provideFileManagerRename(), which runs directly in FileManager.vue's own setup() — the same
// instance that provides navigation, which a component can never inject its own provide() call
// from.
//
// Like useDeleteItemMutation (and unlike useMoveItemMutation), this deliberately does NOT also
// take `settings`: renameItem addresses purely by the item's own id/driveId (already resolved on
// every FileSystemItem the UI hands it), so there's no siteId lookup involved the way move's
// getItemReference() needs.
export const useRenameItemMutation = (
  navigation: Pick<FileManagerNavigationState, 'currentPath' | 'navigateTo'>,
) => {
  const queryClient = useQueryClient();
  const { currentPath, navigateTo } = navigation;

  return useMutation({
    mutationKey: ['directoryService', 'renameItem'],
    mutationFn: ({ item, newName }: RenameItemVariables) =>
      sharePointDriveService.renameItem(item, newName),
    onSuccess: (renamedItem, { item }) => {
      // Rename never changes an item's parent, so — unlike move (source + destination) or delete
      // (parent + grandparent, for the hasFolderChildren flip) — only the parent's own children list
      // needs invalidating; the child count under that parent never changes.
      const parentPath = getParentPath(item.path);
      queryClient.invalidateQueries({
        queryKey: directoryKeys.children(parentPath),
      });

      // Same path-follow reasoning as move's onSuccess: if the renamed item is a directory that is
      // the current folder or an ancestor of it, follow it to its new (renamed) path.
      if (
        item.type === 'directory' &&
        isAncestorOrSelf(item.path, currentPath.value)
      ) {
        navigateTo(renamedItem.path + currentPath.value.slice(item.path.length), { replace: true });
      }
    },
    onError: (error, { item, newName }) => {
      const text =
        error instanceof GraphRequestError && error.status === 409
          ? t('fileManager.useRenameItemMutation.alreadyExists', { name: newName })
          : t('fileManager.useRenameItemMutation.failedToRename', { name: item.name });
      notificationService.open({ text, type: 'danger' });
    },
  });
};
